import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Copy, ExternalLink, Crown, Sparkles, TrendingUp, DollarSign, Users,
  Trophy, Award, ShieldCheck, Globe, ArrowRight, Share2,
} from "lucide-react";
import { nativeShare, isNative } from "../lib/mobile";
import LandingPagesList from "../components/LandingPagesList";
import AmbassadorAttributionCard from "../components/AmbassadorAttributionCard";

const TIER_META = {
  advocate:   { name: "Advocate",            icon: Sparkles,   color: "text-primary" },
  ambassador: { name: "Ambassador",          icon: ShieldCheck,color: "text-secondary" },
  elite:      { name: "Elite Ambassador",    icon: Crown,      color: "text-secondary" },
  regional:   { name: "Regional Director",   icon: Globe,      color: "text-secondary" },
};

export default function AmbassadorDashboard() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meR, lbR] = await Promise.all([
          api.get("/ambassador/me"),
          api.get("/ambassador/leaderboard?period=30d&limit=10"),
        ]);
        if (!cancelled) {
          setData(meR.data);
          setBoard(lbR.data.rows);
        }
      } catch {
        /* swallow */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copyLink = async () => {
    if (!data?.referralUrl) return;
    const r = await nativeShare({
      title: "Join me on Cauldron",
      text: "I'm part of the Cauldron Ambassador Program — join through me:",
      url: data.referralUrl,
    });
    if (r.method === "clipboard") toast.success("Link copied — paste it anywhere");
    else if (r.method === "native" || r.method === "web-share") { /* OS handled */ }
    else toast.error("Couldn't share — try copying manually");
  };

  if (!data) {
    return <Layout><div className="text-foreground/60 p-10">Loading…</div></Layout>;
  }

  // Non-ambassador state → apply CTA
  if (!data.isAmbassador) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16" data-testid="dashboard-not-ambassador">
          <div className="label-mystic text-secondary">Ambassador Program</div>
          <h1 className="font-serif text-4xl mt-3">You&apos;re not yet an Ambassador.</h1>
          <p className="text-foreground/70 mt-4 leading-relaxed">
            Apply to join the program and unlock recurring USD commissions, CAUL rewards, and
            featured placement in the app.
          </p>
          {data.referralUrl && (
            <div className="parchment p-5 mt-8">
              <div className="label-mystic text-foreground/65 mb-2">Your personal referral link</div>
              <div className="flex items-center gap-2">
                <input readOnly value={data.referralUrl} className="flex-1 bg-input border border-secondary/20 p-2.5 rounded-sm font-mono text-xs"
                  data-testid="user-ref-link" />
                <button onClick={() => { navigator.clipboard.writeText(data.referralUrl); toast.success("Copied"); }}
                  className="border border-secondary/45 hover:bg-secondary/10 px-3 py-2.5 rounded-sm">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-foreground/55 mt-2">Even before you&apos;re an Ambassador, this link rewards you with CAUL when someone signs up.</p>
            </div>
          )}
          <Link to="/ambassadors/apply"
            className="mt-10 inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/85 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold"
            data-testid="dashboard-apply-btn">
            Apply now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Layout>
    );
  }

  const TierIcon = TIER_META[data.tier]?.icon || Sparkles;
  const rateCents = data.settings?.commissionUsdCentsByTier?.[data.tier] || 500;
  const nextTier = {
    advocate:   { key: "ambassador", goal: data.settings?.tierThresholds?.ambassador || 10 },
    ambassador: { key: "elite",      goal: data.settings?.tierThresholds?.elite || 50 },
    elite:      { key: "regional",   goal: data.settings?.tierThresholds?.regional || 250 },
    regional:   null,
  }[data.tier];
  const refsToNext = nextTier ? Math.max(0, nextTier.goal - data.totalPractitionerReferrals) : 0;
  const progressPct = nextTier ? Math.min(100, (data.totalPractitionerReferrals / nextTier.goal) * 100) : 100;

  return (
    <Layout>
      <div className="space-y-8 py-2" data-testid="ambassador-dashboard">
        {/* ─── HEADER ─── */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <TierIcon className={`w-5 h-5 ${TIER_META[data.tier]?.color || ""}`} />
            <span className="label-mystic text-secondary" data-testid="tier-label">{TIER_META[data.tier]?.name || data.tier}</span>
            {data.isFounding && (
              <span className="inline-flex items-center gap-1 bg-secondary/15 border border-secondary/45 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider text-secondary" data-testid="founding-badge">
                <Crown className="w-3 h-3" /> Founding #{data.foundingNumber}
              </span>
            )}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl">Ambassador Dashboard</h1>
        </div>

        {/* ─── REFERRAL LINK ─── */}
        <div className="parchment p-6" data-testid="ref-link-card">
          <div className="label-mystic text-secondary mb-3">Your Referral Link</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input readOnly value={data.referralUrl} className="flex-1 min-w-[260px] bg-input border border-secondary/30 p-3 rounded-sm font-mono text-sm" data-testid="ref-link-input" />
            <button onClick={copyLink} className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-5 py-3 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2" data-testid="ref-link-copy">
              {isNative() ? <><Share2 className="w-4 h-4" /> Share</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
            <a href={data.referralUrl} target="_blank" rel="noreferrer" className="border border-secondary/40 hover:bg-secondary/10 px-5 py-3 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
          </div>
          <p className="text-[11px] text-foreground/55 mt-3">
            Every practitioner who subscribes through this link credits you <strong className="text-secondary">${(rateCents / 100).toFixed(2)}/month</strong> for as long as they stay subscribed.
          </p>
        </div>

        {/* ─── LANDING PAGES ─── */}
        <div className="parchment p-6" data-testid="landing-pages-card">
          <div className="flex items-baseline justify-between mb-2">
            <div className="label-mystic text-secondary">Landing Pages</div>
            <Link
              to="/ambassador?tab=landing"
              className="text-xs text-secondary hover:underline"
              data-testid="landing-pages-back-office-link"
            >
              Full back office →
            </Link>
          </div>
          <p className="text-[11px] text-foreground/55 mb-4">
            Home, seekers, 17 practitioner niches, the founding-elders directory, and the ambassador
            program — every URL is pre-tagged with your referral code. Share whichever page best
            fits your audience.
          </p>
          <LandingPagesList compact />
        </div>

        {/* ─── ATTRIBUTION ─── */}
        <AmbassadorAttributionCard days={30} />

        {/* ─── STATS GRID ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Practitioners Recruited"
            value={data.totalPractitionerReferrals}
            sub={`${data.totalSeekerReferrals} seekers`}
            icon={Users} testid="stat-practitioners"
          />
          <StatCard
            label="Projected Monthly USD"
            value={`$${data.projectedMonthlyUsd.toFixed(2)}`}
            sub="recurring run-rate"
            icon={TrendingUp} testid="stat-projected"
          />
          <StatCard
            label="Last 30d Earnings"
            value={`$${data.last30dUsd.toFixed(2)}`}
            sub={`${data.last30dSubs} subscriptions`}
            icon={DollarSign} testid="stat-30d"
          />
          <StatCard
            label="Lifetime USD Earned"
            value={`$${data.totalCommissionUsd.toFixed(2)}`}
            sub={`${data.totalCommissionCaul} CAUL`}
            icon={Award} testid="stat-lifetime"
          />
        </div>

        {/* ─── NEXT TIER PROGRESS ─── */}
        {nextTier && (
          <div className="parchment p-6" data-testid="next-tier-card">
            <div className="flex items-baseline justify-between mb-2">
              <div className="label-mystic text-foreground/65">Next Tier · {TIER_META[nextTier.key].name}</div>
              <div className="text-xs text-foreground/65" data-testid="next-tier-remaining">
                {refsToNext} more practitioner{refsToNext === 1 ? "" : "s"} → unlock
              </div>
            </div>
            <div className="h-2 bg-foreground/10 rounded-full overflow-hidden">
              <div className="h-full bg-secondary transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="text-[11px] text-foreground/55 mt-3">
              At <strong className="text-secondary">{TIER_META[nextTier.key].name}</strong>, your per-sub commission climbs to
              <strong className="text-secondary"> ${((data.settings?.commissionUsdCentsByTier?.[nextTier.key] || 0) / 100).toFixed(2)}/mo</strong>.
            </p>
          </div>
        )}

        {/* ─── QUICK LINKS ─── */}
        <div className="grid sm:grid-cols-3 gap-4">
          <QuickLink to="/ambassadors/kit" icon={Award} label="Marketing Kit" hint="Graphics + swipe copy" testid="ql-kit" />
          <QuickLink to="/ambassadors/leaderboard" icon={Trophy} label="Leaderboard" hint="See top recruiters" testid="ql-board" />
          <QuickLink to="/referrals" icon={Users} label="Detailed Referrals" hint="Per-person breakdown" testid="ql-referrals" />
        </div>

        {/* ─── RECENT COMMISSIONS ─── */}
        <div className="parchment p-6">
          <div className="label-mystic text-secondary mb-4">Recent Commissions</div>
          {data.recentCommissions.length === 0 ? (
            <div className="text-sm text-foreground/60">No commissions yet. Share your link to get started.</div>
          ) : (
            <div className="space-y-2">
              {data.recentCommissions.slice(0, 12).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 border-b border-foreground/5 pb-2 last:border-0" data-testid={`comm-row-${c.id}`}>
                  <div className="text-sm">
                    <div className="text-foreground">{c.referredUserName || "Practitioner"}</div>
                    <div className="text-[11px] text-foreground/50">{c.type} · {new Date(c.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div className="font-serif text-secondary">+${((c.amountUsdCents || 0) / 100).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── INLINE LEADERBOARD PEEK ─── */}
        {board && board.length > 0 && (
          <div className="parchment p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="label-mystic text-secondary">Top 10 · Last 30 days</div>
              <Link to="/ambassadors/leaderboard" className="text-xs text-secondary hover:underline">View full →</Link>
            </div>
            <div className="space-y-2">
              {board.map((r) => (
                <div key={r.userId} className="flex items-center gap-3 text-sm">
                  <div className="font-serif text-secondary w-8">#{r.rank}</div>
                  <div className="flex-1 truncate">{r.name} {r.isFounding && <Crown className="inline w-3 h-3 text-secondary ml-1" />}</div>
                  <div className="text-foreground/60 text-xs">{r.subs} subs</div>
                  <div className="font-serif text-secondary w-20 text-right">${r.usd.toFixed(0)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="parchment p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-mystic text-foreground/60">{label}</div>
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <div className="font-serif text-3xl text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-foreground/55 mt-1">{sub}</div>}
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, hint, testid }) {
  return (
    <Link to={to} className="parchment p-5 hover:border-secondary/45 transition group" data-testid={testid}>
      <Icon className="w-5 h-5 text-secondary mb-3" />
      <div className="font-serif text-lg group-hover:text-secondary transition">{label}</div>
      <div className="text-[11px] text-foreground/55 mt-1">{hint}</div>
    </Link>
  );
}
